import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import { CheckCircle, XCircle, Loader2, Crown } from 'lucide-react';

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [planId, setPlanId] = useState(null);
  const sessionId = searchParams.get('session_id');
  const planParam = searchParams.get('plan');

  useEffect(() => {
    if (sessionId) {
      pollStatus(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollStatus = async (attempts) => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await api.getSubscriptionStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        setPlanId(res.data.plan_id || planParam);
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }
      setTimeout(() => pollStatus(attempts + 1), 2000);
    } catch (error) {
      console.error('Status check failed:', error);
      setStatus('error');
    }
  };

  const planNames = { basic: 'Basic', pro: 'Pro', enterprise: 'Enterprise' };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-blue-600 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-bold mb-2">Activating Your Plan</h2>
              <p className="text-gray-500">Please wait while we process your subscription...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="bg-green-100 rounded-full p-4 inline-block mb-4">
                <Crown className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-700 mb-2">Subscription Activated!</h2>
              <p className="text-gray-600 mb-2">
                You're now on the <strong className="text-blue-600">{planNames[planId] || planId}</strong> plan.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                All features are now unlocked. Enjoy REVA!
              </p>
              <Button onClick={() => navigate('/dashboard')} className="w-full">
                Go to Dashboard
              </Button>
            </>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-red-700 mb-2">
                {status === 'expired' ? 'Session Expired' : 'Activation Issue'}
              </h2>
              <p className="text-gray-600 mb-6">
                Please try again or contact support.
              </p>
              <Button onClick={() => navigate('/settings')} className="w-full">
                Back to Settings
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
